/**
 * Main Challenge Contract Hook
 *
 * Single hook that combines reads and writes
 * This is the PRIMARY hook developers should use
 */

import { useContractCalls } from './useContractCalls';
import { useContractReads } from './useContractReads';

/**
 * All-in-one hook for challenge contract operations
 *
 * @example
 * const {
 *   // Write operations (gasless)
 *   createChallenge,
 *   joinChallenge,
 *   claimRewards,
 *
 *   // Read operations
 *   hasUserJoined,
 *   getChallengeData,
 *
 *   // State
 *   isLoading,
 *   error
 * } = useChallengeContract();
 *
 * // Create a challenge (gasless)
 * await createChallenge({
 *   startDate: Math.floor(Date.now() / 1000),
 *   duration: 7 * 24 * 60 * 60,
 *   stakeAmount: "10"
 * });
 *
 * // Join a challenge (gasless, batched approve + join)
 * await joinChallenge({
 *   challengeAddress: '0x...',
 *   tokenAddress: '0x...',
 *   stakeAmount: "10"
 * });
 *
 * // Check if user joined
 * const joined = await hasUserJoined('0xChallenge...', '0xUser...');
 */
export function useChallengeContract() {
  const {
    createChallenge,
    joinChallenge,
    claimRewards,
    leaveChallenge,
    transferToWallet,
    isLoading: writeLoading,
    error: writeError,
  } = useContractCalls();

  const {
    hasUserJoined,
    getChallengeData,
    getMinChallengeDuration,
    getGracePeriod,
    isLoading: readLoading,
    error: readError,
  } = useContractReads();

  return {
    // Write operations (gasless)
    createChallenge,
    joinChallenge,
    claimRewards,
    leaveChallenge,
    transferToWallet, // For off-chain (marathon) challenge joins

    // Read operations
    hasUserJoined,
    getChallengeData,
    getMinChallengeDuration,
    getGracePeriod,

    // Combined state
    isLoading: writeLoading || readLoading,
    error: writeError || readError,
  };
}
